import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Portes Ouvertes : les fenêtres sont saisies par l'école dans le classeur (onglet Listes,
// colonnes AA/AB) et transmises en prop `poRanges` = [{ debut: "2026-08-24", fin: "…" }].
// Aucune date n'est écrite ici.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

const WEEKDAYS_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

// day-of-week label (French) → JS getDay() index (0=Sun)
export const FR_DAY_TO_IDX = {
  "dimanche": 0, "lundi": 1, "mardi": 2, "mercredi": 3,
  "jeudi": 4, "vendredi": 5, "samedi": 6,
};

function sameDay(a, b) { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }

/**
 * Custom calendar picker — restricts to a specific weekday and highlights Portes Ouvertes.
 * @param {Object} props
 * @param {number|null} props.allowedWeekday - JS getDay() index (0-6). If null, all days allowed.
 * @param {string} props.value - ISO date "YYYY-MM-DD"
 * @param {(iso: string) => void} props.onChange
 * @param {Date} [props.minDate] - Minimum selectable date (default: today)
 * @param {Array<{start: Date, end: Date, label?: string}>} [props.blockedRanges] - Vacances scolaires (dates grisées)
 * @param {Array<{debut: string, fin: string}>} [props.poRanges] - Portes ouvertes lues dans le classeur (essai offert)
 */
export function CalendarPicker({ allowedWeekday, value, onChange, minDate, blockedRanges, poRanges }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const min = minDate || today;
  const fenetres = poRanges || [];
  const enPO = (d) => { const j = iso(d); return fenetres.some((f) => f.debut <= j && j <= f.fin); };

  // Mois affiché à l'ouverture : TOUJOURS le mois courant (ou celui de `minDate`).
  // On ouvrait avant sur la prochaine fenêtre de Portes Ouvertes, ce qui projetait le
  // calendrier des mois plus loin et cachait les semaines réservables tout de suite.
  const initialMonth = useMemo(
    () => new Date(min.getFullYear(), min.getMonth(), 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [min.getFullYear(), min.getMonth()]
  );
  const [viewMonth, setViewMonth] = useState(initialMonth);

  const selectedDate = value ? new Date(value + "T00:00:00") : null;

  // Build grid: 6 rows × 7 cols, aligned to Sunday first
  const cells = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startOffset = first.getDay(); // 0=Sun
    const rows = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i - startOffset + 1);
      rows.push(d);
    }
    return rows;
  }, [viewMonth]);

  const prevMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const nextMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  return (
    <div className="rounded-2xl p-4 border" style={{ background: "#fff", borderColor: "var(--border-soft)" }} data-testid="calendar-picker">
      {/* Nav */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prevMonth} data-testid="cal-prev" className="p-2 rounded-full hover:bg-[var(--primary-tint)]"><ChevronLeft size={18} /></button>
        <div className="font-display text-lg capitalize">
          {MONTHS_FR[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </div>
        <button type="button" onClick={nextMonth} data-testid="cal-next" className="p-2 rounded-full hover:bg-[var(--primary-tint)]"><ChevronRight size={18} /></button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS_FR.map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-[var(--muted)] py-1">{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === viewMonth.getMonth();
          const isPast = d < min;
          const wrongDow = allowedWeekday !== null && allowedWeekday !== undefined && d.getDay() !== allowedWeekday;
          const inPO = enPO(d);
          // Une plage marquée `poAutorisee` (essais suspendus pour le spectacle) laisse passer
          // les Portes Ouvertes : c'est justement la fenêtre où l'essai redevient possible.
          const bloquee = (blockedRanges || []).find((r) => d >= r.start && d <= r.end);
          const inBlocked = bloquee && bloquee.poAutorisee && inPO ? null : bloquee;
          const disabled = !inMonth || isPast || wrongDow || !!inBlocked;
          const isSelected = selectedDate && sameDay(d, selectedDate);
          const isToday = sameDay(d, today);

          let bg = "transparent";
          let color = "var(--text)";
          let border = "1px solid transparent";
          let fontWeight = 400;

          if (!inMonth) { color = "#CBD5E1"; }
          else if (isSelected) { bg = "var(--primary)"; color = "#fff"; fontWeight = 700; }
          else if (inBlocked && !wrongDow && !isPast) { bg = "#FEE2E2"; color = "#7F1D1D"; border = "1px solid #FCA5A5"; }
          else if (disabled) { color = "#CBD5E1"; }
          else if (inPO) { bg = "#DCFCE7"; color = "#065F46"; fontWeight = 600; border = "1px solid #86EFAC"; }
          if (isToday && !isSelected) { border = "2px solid var(--primary)"; }

          const title = inBlocked ? (inBlocked.label || "Vacances scolaires — pas de cours") : undefined;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange(iso(d))}
              data-testid={`cal-day-${iso(d)}`}
              className={`aspect-square rounded-lg text-sm relative transition ${disabled ? "cursor-not-allowed" : "hover:brightness-95"}`}
              style={{ background: bg, color, border, fontWeight }}
              title={title}
              aria-label={`${d.getDate()} ${MONTHS_FR[d.getMonth()]}${inPO ? " (Portes Ouvertes — cours d'essai gratuit)" : ""}${inBlocked ? ` (${inBlocked.label || "Vacances scolaires"})` : ""}`}
            >
              {d.getDate()}
              {inPO && !disabled && !isSelected && (
                <span aria-hidden className="absolute top-0.5 right-1 text-[10px]">★</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-[var(--border-soft)] flex flex-wrap gap-x-4 gap-y-2 text-xs">
        {fenetres.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ background: "#DCFCE7", border: "1px solid #86EFAC" }}></span>
            <span className="text-[var(--text)]"><strong>★ Portes Ouvertes</strong> · essai gratuit</span>
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: "var(--primary)" }}></span>
          <span className="text-[var(--text)]">Date sélectionnée</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: "#FEE2E2", border: "1px solid #FCA5A5" }}></span>
          <span className="text-[var(--text)]">
            {(blockedRanges || []).some((r) => r.poAutorisee)
              ? "Vacances scolaires, jours fériés & préparation du spectacle"
              : "Vacances scolaires & jours fériés"}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ border: "2px solid var(--primary)" }}></span>
          <span className="text-[var(--text)]">Aujourd'hui</span>
        </span>
        {(allowedWeekday !== null && allowedWeekday !== undefined) && (
          <span className="text-[var(--muted)] italic">Seuls les {["dimanches","lundis","mardis","mercredis","jeudis","vendredis","samedis"][allowedWeekday]} sont disponibles pour ce cours.</span>
        )}
      </div>
    </div>
  );
}

/**
 * Generate an .ics calendar event string for a trial course.
 * @param {Object} evt
 * @param {string} evt.title
 * @param {string} evt.date - "YYYY-MM-DD"
 * @param {string} evt.start - "HH:mm"
 * @param {string} evt.end   - "HH:mm"
 * @param {string} evt.description
 * @param {string} evt.location
 */
export function buildICS({ title, date, start, end, description, location }) {
  const dt = (d, t) => `${d.replace(/-/g,"")}T${t.replace(":","")}00`;
  const uid = `essai-${date}-${start}-${Math.random().toString(36).slice(2,8)}@1000arts.ch`;
  const now = new Date().toISOString().replace(/[-:]/g,"").replace(/\.\d{3}Z$/,"Z");
  const esc = (s) => String(s || "").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//1000 ARTS//Cours d'essai//FR",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${dt(date, start)}`,
    `DTEND:${dt(date, end)}`,
    `SUMMARY:${esc(title)}`,
    `DESCRIPTION:${esc(description)}`,
    `LOCATION:${esc(location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

/** Download the .ics file client-side. */
export function downloadICS(icsContent, filename) {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "1000arts-cours-essai.ics";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
